import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import PropTypes from 'prop-types';

const ScheduleDialog = ({ open, onClose, onConfirm, action, resourceName, onCloseParent }) => {
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const getActionText = () => (action === 'start' ? 'Start' : 'Stop');
  const getActionColor = () => (action === 'start' ? 'success' : 'error');
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0); 
    return now;
  };

  const handleScheduleClick = () => {
    if (!selectedDateTime) {
      setError('Please pick a date and time.');
      return;
    }
        if (selectedDateTime <= new Date()) {
      setError('Please select a future time today or later.');
      return;
    }
    
    setShowConfirm(true);
  };

  const handleFinalConfirm = async () => {
    setLoading(true); 
    try {
      await onConfirm(selectedDateTime); 
      if (onCloseParent) {
        onCloseParent();
      }
    } finally {
      setLoading(false); 
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSelectedDateTime(null); 
    setError('');
    setShowConfirm(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 'bold' }}>
        Schedule {getActionText()} Instance
      </DialogTitle>

      <DialogContent>
        {!showConfirm ? (
          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Select Date & Time"
                value={selectedDateTime}
                onChange={(v) => {
                  setSelectedDateTime(v);
                  setError('');
                }}
                
                //  Applies our "Top of the Hour" logic
                minDateTime={getMinDateTime()} 

                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(error),
                    helperText: error || 'Select when you want to trigger the action',
                  },
                  
                  popper: {
                    sx: {
                      position: 'fixed !important',
                      top: '50% !important',
                      left: '50% !important',
                      transform: 'translate(-50%, -50%) !important',
                      zIndex: 9999,
                    }
                  },
                  
                  actionBar: { actions: ['cancel', 'accept'] }
                }}
              />
            </LocalizationProvider>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography>
              Are you sure you want to <b>{getActionText()}</b> this instance at:
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 600 }}>
              {selectedDateTime.toLocaleString()}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!showConfirm ? (
          <>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleScheduleClick}
              variant="contained"
              color={getActionColor()}
            >
              Schedule {getActionText()}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setShowConfirm(false)} color="inherit" disabled={loading}>
              Back
            </Button>
            <Button
              onClick={handleFinalConfirm}
              variant="contained"
              color={getActionColor()}
              disabled={!selectedDateTime || loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Yes, Schedule'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

ScheduleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  action: PropTypes.oneOf(['start', 'stop']).isRequired,
  resourceName: PropTypes.string,
  onCloseParent: PropTypes.func,
};

export default ScheduleDialog;